import { FC } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Profile } from '../../../../global-state/types';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../../theme';
import { Permissions } from '../../../profile/permissions/Permissions';
import { Typography } from '../../../../stories/atoms';
import { employeesTranslations, profileTranslations } from '../../../../translations';
import { useUser } from '../../../../hooks';
import { isSupportedSuamRole, RoleMapping } from '../../../../utils';
import Copy from '../../../../stories/molecules/copy/Copy';

export type EmployeeOverviewModalProps = {
  employee: Profile;
  isModalOpen: boolean;
  isCurrentUser: boolean;
  handleClose: () => void;
};

const {
  seeDetailsModal: { detailedInfo },
  employees: { other },
} = employeesTranslations;

const { name, upn: emailTranslation, fullName, role: roleTranslation, staffId } = profileTranslations;

export const EmployeeOverviewModal: FC<EmployeeOverviewModalProps> = ({ employee }) => {
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const { t } = useTranslation();
  const {
    get: { currentStoreId },
  } = useUser();

  const { firstName, lastName, assignments, upn, id } = employee;

  const selectedEmployeeRole = [
    [
      ...new Set(
        assignments
          ?.filter(({ locationCode }) => locationCode === currentStoreId)
          .map(({ suamRoleCode }) => suamRoleCode),
      ),
    ].map(
      (suamRoleCode, index) =>
        (index ? ', ' : '') +
        (isSupportedSuamRole(suamRoleCode)
          ? t(`employees.roles.${RoleMapping[suamRoleCode]}`, RoleMapping[suamRoleCode])
          : t(other.key, other.defaultValue)),
    ),
  ];

  const employeeInfoData = [
    {
      label: t(name.key, name.defaultValue),
      value: t(fullName.key, {
        defaultValue: fullName.defaultValue,
        firstName,
        lastName,
      }),
    },
    {
      label: t(roleTranslation.key, roleTranslation.defaultValue),
      value: selectedEmployeeRole,
    },
    {
      label: t(staffId.key, staffId.defaultValue),
      value: (
        <RowFlexContainer gap={[2]} alignItems="center">
          <Typography>{id}</Typography>
          <Copy textToCopy={id} />
        </RowFlexContainer>
      ),
    },
    {
      label: t(emailTranslation.key, emailTranslation.defaultValue),
      value: (
        <RowFlexContainer gap={[2]} alignItems="center">
          <Typography>{upn}</Typography>
          <Copy textToCopy={upn} />
        </RowFlexContainer>
      ),
    },
  ];

  return (
    <ColumnFlexContainer
      data-testid="permission-view-modal"
      width={isMobile ? '100%' : '50vw'}
      padding={isMobile ? [12, 5] : [10]}
      minWidth={'40vw'}
      height={'90vh'}
      gap={[5]}
    >
      <ColumnFlexContainer data-testid="employee-info" gap={[2]}>
        <Typography type="h4">{t(detailedInfo.key, detailedInfo.defaultValue)}</Typography>

        <ColumnFlexContainer gap={[2]}>
          {employeeInfoData.map((item) => (
            <RowFlexContainer key={item.label} alignItems="center">
              <RowFlexContainer width={[20]} justifyContent="start">
                <Typography>{item.label}</Typography>
              </RowFlexContainer>

              <ColumnFlexContainer flex={1}>
                <Typography>{item.value}</Typography>
              </ColumnFlexContainer>
            </RowFlexContainer>
          ))}
        </ColumnFlexContainer>
      </ColumnFlexContainer>

      <Permissions isMobile={isMobile} employee={employee} />
    </ColumnFlexContainer>
  );
};
